import { useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Share2, RefreshCw, TrendingUp, MoreVertical, CalendarDays } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { UnifiedLandscapeTable } from "@/components/keyword-landscape-output";
import { ShareLinkModal } from "@/components/shared/ShareLinkModal";
import { TruncatedText } from "@/components/ui/truncated-text";
import { getLandscapeOutputById } from "@/data/mockLandscapeOutputData";
import { mockHeroProducts } from "@/data/mockLandscapeData";
import { resolveProductImage } from "@/assets/products";
import { toast } from "sonner";
import { RoutePath } from "@/routes";
import { cn } from "@/lib/utils";
import {
  formatKeywordPendingMoney,
  formatKeywordPendingNumber,
  formatKeywordPendingRank,
} from "@/utils/keywordPendingDisplay";
import { KeywordRanking, LandscapeTab } from "@/types";

export default function KeywordLandscapeOutput() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<LandscapeTab>(LandscapeTab.Keyword);
  const filteredKeywordsRef = useRef<KeywordRanking[]>([]);

  const landscapeData = instanceId ? getLandscapeOutputById(instanceId) : undefined;

  // Find all versions from the hub data by matching the instance ID prefix
  const allInstances = useMemo(() => {
    if (!instanceId) return [];
    const prefix = instanceId.replace(/-\d+$/, "");
    for (const product of mockHeroProducts) {
      for (const landscape of product.landscapes) {
        const match = landscape.instances.find((inst) => inst.id.startsWith(prefix));
        if (match) {
          return landscape.instances.map((inst) => ({
            id: inst.id,
            date: inst.date,
          }));
        }
      }
    }
    return [];
  }, [instanceId]);

  // Find the hero product from hub data for image/title/ASIN
  const hubHeroProduct = useMemo(() => {
    if (!instanceId) return undefined;
    for (const product of mockHeroProducts) {
      for (const landscape of product.landscapes) {
        if (landscape.instances.some((inst) => inst.id === instanceId)) {
          return product;
        }
      }
    }
    return undefined;
  }, [instanceId]);

  // Find all landscapes for this hero product (for "Choose different landscape" dropdown)
  const allLandscapes = useMemo(() => {
    if (!hubHeroProduct) return [];
    return hubHeroProduct.landscapes.map((l) => ({
      id: l.instances[0]?.id,
      phrase: l.prioritySearchPhrase,
    }));
  }, [hubHeroProduct]);

  // Find the current landscape's first instance for the dropdown value
  const currentLandscapeFirstInstance = useMemo(() => {
    if (!instanceId || !hubHeroProduct) return "";
    for (const landscape of hubHeroProduct.landscapes) {
      if (landscape.instances.some((inst) => inst.id === instanceId)) {
        return landscape.instances[0]?.id || "";
      }
    }
    return "";
  }, [instanceId, hubHeroProduct]);

  const heroIndex = landscapeData ? landscapeData.products.findIndex((p) => p.isHero) : -1;

  const handleFilteredKeywordsChange = useCallback((keywords: KeywordRanking[]) => {
    filteredKeywordsRef.current = keywords;
  }, []);

  if (!landscapeData) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <h2 className="text-xl font-semibold text-foreground mb-2">
          Landscape not found
        </h2>
        <p className="text-muted-foreground mb-4">
          The keyword landscape you're looking for doesn't exist.
        </p>
        <Button onClick={() => navigate(RoutePath.KeywordLandscapes)}>
          <ArrowLeft className="h-4 w-4" />
          Back to Keyword Landscapes
        </Button>
      </div>
    );
  }

  const handleInstanceChange = (newInstanceId: string) => {
    navigate(`${RoutePath.KeywordLandscapes}/${newInstanceId}`);
  };

  const handleLandscapeChange = (landscapeFirstInstanceId: string) => {
    navigate(`${RoutePath.KeywordLandscapes}/${landscapeFirstInstanceId}`);
  };

  const escapeCsvCell = (val: unknown): string => {
    if (val === null || val === undefined) return "";
    const s = String(val);
    if (/[",\n\r]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const handleExport = () => {
    if (!landscapeData) return;
    const keywords =
      filteredKeywordsRef.current.length > 0
        ? filteredKeywordsRef.current
        : landscapeData.keywords;

    console.log("[CSV export] start, count:", keywords.length);

    try {
      const headers = [
        "Keyword",
        "Hero Rank",
        "Competitors",
        "Search Volume",
        "KPS",
        "Relevance",
        "Trend %",
        "PPC Max Bid",
      ];

      const parts: string[] = [];
      parts.push(headers.join(",") + "\n");

      for (const kw of keywords) {
        const row = [
          escapeCsvCell(kw.keyword),
          escapeCsvCell(formatKeywordPendingRank(kw.ranks[heroIndex] ?? null)),
          escapeCsvCell(kw.competitors),
          escapeCsvCell(kw.exactVol),
          escapeCsvCell(formatKeywordPendingNumber(kw.kpg)),
          escapeCsvCell(formatKeywordPendingNumber(kw.hero_relevance)),
          escapeCsvCell(`${kw.search_volume}%`),
          escapeCsvCell(formatKeywordPendingMoney(kw.ppc_max_bid)),
        ].join(",");
        parts.push(row + "\n");
      }

      const blob = new Blob(parts, { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const slug = landscapeData.prioritySearchPhrase
        .replace(/\s+/g, "-")
        .toLowerCase();
      const date = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `landscape-${slug}-${date}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${keywords.length} keywords to CSV.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    }
  };

  return (
    <div className="space-y-4 min-w-0">
        {/* Product identity bar */}
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-primary hover:text-primary/80 shrink-0"
            onClick={() => navigate(RoutePath.KeywordLandscapes)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          {hubHeroProduct && (
            <>
              <div
                className="w-10 h-10 rounded border-2 border-card bg-white overflow-hidden shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate(RoutePath.KeywordLandscapes)}
              >
                <img
                  src={hubHeroProduct.imageUrl}
                  alt={hubHeroProduct.title}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="min-w-0 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate(RoutePath.KeywordLandscapes)}>
                <p className="text-sm font-semibold text-foreground">
                  <TruncatedText fullValue={hubHeroProduct.title}>
                    {hubHeroProduct.title.length > 60
                      ? hubHeroProduct.title.slice(0, 60) + "..."
                      : hubHeroProduct.title}
                  </TruncatedText>
                </p>
                <p className="text-xs text-primary">{hubHeroProduct.asin}</p>
              </div>
            </>
          )}
          <div className="ml-auto flex items-center gap-2">
            {allLandscapes.length > 1 && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground whitespace-nowrap">Jump to search phrase</span>
                <Select value={currentLandscapeFirstInstance} onValueChange={handleLandscapeChange}>
                  <SelectTrigger className="w-[200px] text-xs rounded-[8px] bg-card">
                    <SelectValue placeholder="Choose landscape" />
                  </SelectTrigger>
                  <SelectContent className="bg-card rounded-[8px]">
                    {allLandscapes.map((l) => (
                      <SelectItem key={l.id} value={l.id} className="text-xs capitalize">
                        {l.phrase}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Full-width content */}
        <div className="bg-card flex-1 -mx-6 -mb-10 px-6 pt-5 pb-4 min-w-0">
          {/* Priority Search Phrase + Refresh */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-end gap-3">
              <div>
                <p className="text-[10px] font-semibold tracking-widest text-muted-foreground uppercase mb-1">
                  Priority Search Phrase
                </p>
                <h1 className="text-2xl font-bold text-foreground capitalize">
                  {landscapeData.prioritySearchPhrase}
                </h1>
              </div>
              <button className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors mb-1">
                <TrendingUp className="h-3.5 w-3.5" />
                Trend Report
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Select value={instanceId} onValueChange={handleInstanceChange}>
                <SelectTrigger className="w-[160px] text-xs rounded-[8px]">
                  <CalendarDays className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <SelectValue placeholder="Select date" />
                </SelectTrigger>
                <SelectContent className="bg-popover rounded-[8px]">
                  {allInstances.map((instance) => (
                    <SelectItem key={instance.id} value={instance.id} className="text-xs">
                      {instance.date}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" className="h-9 gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" />
                <span className="text-xs">Refresh</span>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-popover border border-border rounded-[8px]">
                  <DropdownMenuItem onClick={handleExport} className="cursor-pointer">
                    <Download className="mr-2 h-4 w-4" />
                    Export
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShareModalOpen(true)} className="cursor-pointer">
                    <Share2 className="mr-2 h-4 w-4" />
                    Share
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Analysis tabs + Stats */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-[8px]">
              <button
                onClick={() => setActiveTab(LandscapeTab.Keyword)}
                className={cn(
                  "px-3.5 py-1.5 text-sm font-medium rounded-[8px] transition-all",
                  activeTab === "keyword"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                Keyword Analysis
              </button>
              <button
                onClick={() => setActiveTab(LandscapeTab.Competitor)}
                className={cn(
                  "px-3.5 py-1.5 text-sm font-medium rounded-[8px] transition-all",
                  activeTab === "competitor"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                Competitor Analysis
              </button>
            </div>

            {/* Stats box */}
            <div className="flex items-center border border-border rounded-[3px] divide-x divide-border">
              <div className="px-4 py-2 text-center">
                <p className="text-[10px] text-muted-foreground font-medium">Relevant Keywords</p>
                <p className="text-sm font-semibold text-foreground">{landscapeData.totals.relevantKeywords.toLocaleString()}</p>
              </div>
              <div className="px-4 py-2 text-center">
                <p className="text-[10px] text-muted-foreground font-medium">Exact Volume</p>
                <p className="text-sm font-semibold text-foreground">{landscapeData.totals.exactVolume.toLocaleString()}</p>
              </div>
              <div className="px-4 py-2 text-center">
                <p className="text-[10px] text-muted-foreground font-medium">Monthly Units</p>
                <p className="text-sm font-semibold text-foreground">{landscapeData.totals.monthlyUnits.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Table */}
          <UnifiedLandscapeTable
            data={landscapeData}
            onInstanceChange={handleInstanceChange}
            activeTab={activeTab}
            onFilteredKeywordsChange={handleFilteredKeywordsChange}
          />
        </div>

        <ShareLinkModal
          open={shareModalOpen}
          onOpenChange={setShareModalOpen}
          shareableType="landscape"
          shareableId={instanceId}
          resourceName={landscapeData.prioritySearchPhrase}
        />
    </div>
  );
}
